
import React from "react";
import { Link } from "react-router-dom";
import {
  MapPin,
  Heart,
  Share2,
  ArrowRight,
  Building2,
  Ruler,
  Landmark,
} from "lucide-react";

/* ============================================================
   PROPERTY DATA
============================================================ */

const properties = [
  {
    id: "premium-french-rowhouses",
    title: "Premium French-Inspired Rowhouses",
    location: "Soukya Road, Whitefield, Bengaluru",
    status: "EOI Open",
    type: "Rowhouse",
    area: "20 Acres",
    units: "242 Rowhouses",
    highlight: "Near Whitefield",

    image:
      "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=85",

    // Individual property page
    url: "/premium-french-rowhouses",

    // Google Maps
    mapUrl: "https://maps.app.goo.gl/hc4TMzhPXom1rvBMA",

    description:
      "Ultra-premium French-inspired rowhouses with elegant architecture, Vastu-compliant homes and central courtyards.",
  },

  {
    id: "brigade-granada",
    title: "Brigade Granada",
    location: "Seegahalli Road, East Bengaluru",
    status: "EOI Open",
    type: "Township",
    area: "40+ Acres",
    units: "55-Floor Towers",
    highlight: "Brigade Group",

    image:
      "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1200&q=85",

    // Individual property page
    url: "/brigade-granada",

    // Google Maps
    mapUrl: "https://maps.app.goo.gl/dwWNkRhi43Wo37we6",

    description:
      "A landmark mixed-use township with excellent connectivity to Whitefield, Kadugodi, ITPL and KR Puram.",
  },

  {
    id: "sanjeevini-heliconia",
    title: "Sanjeevini Heliconia",
    location: "Soukya Road, Whitefield, Bengaluru",
    status: "RERA Registered",
    type: "Residential",
    area: "Premium Homes",
    units: "Exclusive Residences",
    highlight:
      "PRM/KA/RERA/1250/304/PR/180726/008826",

    image:
      "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1200&q=85",

    // Individual property page
    url: "/sanjeevini-heliconia",

    mapUrl: "https://maps.app.goo.gl/U6eeRR7hYbMgZ2gf8",

    description:
      "An exclusive residential landmark combining refined architecture, serenity and convenient access to ITPL and Whitefield.",
  },
];
/* ============================================================
   PROPERTY SECTION
============================================================ */

const PropertySection = () => {
  /* ============================================================
     WHATSAPP SHARE
  ============================================================ */

  const handleShare = (e, property) => {
    e.preventDefault();
    e.stopPropagation();

    const message = `🏡 *${property.title}*

📍 ${property.location}

✨ Type: ${property.type}
📐 Development: ${property.area}
🏢 Project: ${property.units}

${property.description}

🔗 View Property:
${window.location.origin}${property.url}`;

    const whatsappUrl = `https://wa.me/918088687429?text=${encodeURIComponent(
      message
    )}`;

    window.open(whatsappUrl, "_blank");
  };

  /* ============================================================
     FAVORITE
  ============================================================ */

  const handleFavorite = (e, property) => {
    e.preventDefault();
    e.stopPropagation();

    console.log(`Added to favorites: ${property.title}`);
  };

  return (
    <section className="w-full bg-gradient-to-b from-[#F8FAFC] to-[#EEF2F7] py-16 overflow-hidden">
      <div className="w-full px-4 sm:px-6 lg:px-10 xl:px-14">

        {/* ======================================================
            HEADER
        ====================================================== */}

        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">

          {/* LEFT CONTENT */}

          <div className="max-w-2xl">

            {/* LABEL */}

            <span className="inline-flex items-center gap-2 bg-red-50 text-red-600 border border-red-100 px-4 py-2 rounded-full text-xs sm:text-sm font-semibold mb-4">
              <Building2 size={15} />
              Premium Properties
            </span>

            {/* HEADING */}

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tight text-gray-900 leading-tight">
              Properties in{" "}
              <span className="text-red-600">
                Bengaluru
              </span>
            </h2>

            {/* DESCRIPTION */}

            <p className="text-gray-500 text-base sm:text-lg mt-4 leading-relaxed max-w-xl">
              Explore premium residential communities and landmark
              developments across Bengaluru's fastest-growing corridors.
            </p>

          </div>

          {/* VIEW ALL BUTTON */}

          <Link
            to="/properties"
            className="group bg-red-600 hover:bg-red-700 text-white px-6 py-3.5 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-red-500/30 hover:-translate-y-1 flex items-center gap-3 w-fit"
          >
            View All Properties

            <ArrowRight
              size={17}
              className="group-hover:translate-x-1 transition"
            />
          </Link>

        </div>

        {/* ======================================================
            PROPERTY GRID
        ====================================================== */}

        <div className="flex lg:grid lg:grid-cols-3 gap-6 overflow-x-auto lg:overflow-visible snap-x snap-mandatory pb-4 no-scrollbar">

          {properties.map((property) => (

            <Link
              key={property.id}
              to={property.url}
              className="group min-w-[86%] sm:min-w-[58%] lg:min-w-0 bg-white rounded-[24px] overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 border border-gray-100 snap-start block"
            >

              {/* ==================================================
                  IMAGE
              ================================================== */}

              <div className="relative overflow-hidden h-[235px]">

                <img
                  src={property.image}
                  alt={property.title}
                  className="h-full w-full object-cover group-hover:scale-105 transition duration-700"
                  loading="lazy"
                />

                {/* IMAGE OVERLAY */}

                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent pointer-events-none" />

                {/* PROPERTY TYPE */}

                <div className="absolute top-4 left-4">

                  <span className="bg-white/95 backdrop-blur-md text-gray-900 text-xs px-3.5 py-1.5 rounded-full font-semibold shadow-md">
                    {property.type}
                  </span>

                </div>

                {/* ==================================================
                    ACTION BUTTONS
                ================================================== */}

                <div className="absolute top-4 right-4 flex items-center gap-2">

                  {/* FAVORITE */}

                  <button
                    type="button"
                    onClick={(e) =>
                      handleFavorite(e, property)
                    }
                    className="w-9 h-9 rounded-full bg-white/90 backdrop-blur-md flex items-center justify-center hover:bg-white hover:scale-105 transition shadow-md"
                    aria-label={`Add ${property.title} to favorites`}
                  >
                    <Heart
                      size={16}
                      className="text-gray-700 hover:text-red-500 transition"
                    />
                  </button>

                  {/* WHATSAPP SHARE */}

                  <button
                    type="button"
                    onClick={(e) =>
                      handleShare(e, property)
                    }
                    className="w-9 h-9 rounded-full bg-[#25D366] flex items-center justify-center hover:scale-105 transition shadow-md"
                    aria-label={`Share ${property.title}`}
                  >
                    <Share2
                      size={16}
                      className="text-white"
                    />
                  </button>

                </div>

                {/* ==================================================
                    STATUS
                ================================================== */}

                <div className="absolute bottom-4 left-4">

                  <span className="inline-flex items-center gap-2 bg-white/95 backdrop-blur-md rounded-xl px-4 py-2 shadow-lg">

                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />

                    <span className="text-sm font-bold text-gray-900">
                      {property.status}
                    </span>

                  </span>

                </div>

              </div>

              {/* ==================================================
                  CARD CONTENT
              ================================================== */}

              <div className="p-5">

                {/* TITLE */}

                <h3 className="text-xl font-bold text-gray-900 group-hover:text-red-600 transition line-clamp-1">
                  {property.title}
                </h3>

                {/* LOCATION */}

                <div className="flex items-start gap-2 text-gray-500 text-sm mt-2.5">

                  <MapPin
                    size={16}
                    className="text-red-500 mt-0.5 flex-shrink-0"
                  />

                  <span className="line-clamp-1">
                    {property.location}
                  </span>

                </div>

                {/* DESCRIPTION */}

                <p className="text-gray-500 text-sm leading-relaxed mt-3 line-clamp-2">
                  {property.description}
                </p>

                {/* ==================================================
                    FEATURES
                ================================================== */}

                <div className="grid grid-cols-2 gap-2.5 mt-5">

                  {/* DEVELOPMENT */}

                  <div className="bg-gray-50 rounded-xl px-3 py-3 border border-gray-100 flex items-center gap-2.5">

                    <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">

                      <Ruler
                        size={15}
                        className="text-red-600"
                      />

                    </div>

                    <div className="min-w-0">

                      <p className="text-[10px] text-gray-400 uppercase tracking-wide">
                        Development
                      </p>

                      <p className="text-sm font-bold text-gray-900 truncate">
                        {property.area}
                      </p>

                    </div>

                  </div>

                  {/* PROJECT */}

                  <div className="bg-gray-50 rounded-xl px-3 py-3 border border-gray-100 flex items-center gap-2.5">

                    <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">

                      <Landmark
                        size={15}
                        className="text-red-600"
                      />

                    </div>

                    <div className="min-w-0">

                      <p className="text-[10px] text-gray-400 uppercase tracking-wide">
                        Project
                      </p>

                      <p className="text-sm font-bold text-gray-900 truncate">
                        {property.units}
                      </p>

                    </div>

                  </div>

                </div>

                {/* ==================================================
                    CTA
                ================================================== */}

                <div className="mt-5 flex gap-2">

                  {/* EXPLORE PROPERTY */}

                  <span className="flex-1 bg-gray-900 group-hover:bg-red-600 text-white py-3 rounded-xl font-semibold text-sm transition-all duration-300 flex items-center justify-center gap-2">

                    Explore Property

                    <ArrowRight
                      size={16}
                      className="group-hover:translate-x-1 transition"
                    />

                  </span>

                  {/* CONTACT BUTTON */}

                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.open(
                        "https://wa.me/message/MDG2ZSZNG6P6G1",
                        "_blank",
                        "noopener,noreferrer"
                      );
                    }}
                    className="px-4 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold text-sm transition-all duration-300 flex items-center justify-center shadow-md hover:shadow-red-500/20"
                    aria-label={`Contact about ${property.title}`}
                  >
                    Contact
                  </button>

                  {/* GOOGLE MAP */}

                  {property.mapUrl && (
                    <a
                      href={property.mapUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        window.open(
                          property.mapUrl,
                          "_blank",
                          "noopener,noreferrer"
                        );
                      }}
                      className="w-12 bg-gray-100 hover:bg-red-50 text-gray-700 hover:text-red-600 rounded-xl flex items-center justify-center transition"
                      aria-label={`View ${property.title} location`}
                    >
                      <MapPin size={17} />
                    </a>
                  )}

                </div>

              </div>

            </Link>

          ))}

        </div>

      </div>
    </section>
  );
};

export default PropertySection;
